import React from 'react';
import fr from 'facet-rest';
import modalConsts from 'Consts/ModalNames';
import { openModal } from 'Helpers/ModalHelper';
import {
  FDDialog, FDDialogActions, FDDialogContent, FDDialogTitle,
  FDPrimaryActionButton, FDStack, FDBodyText, FDSelectInput,
} from '@facet-wealth/design';
import {
  LEAD_PLANNER,
} from 'Classes/Agent';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import moment from 'moment';

const AgentDialogLayout = ({
  isAdd = true,
  handleClose,
  openPlannerTransitionDialog,
  refresh,
}) => {
  const { resource, handleChange } = useMetaLayoutContext();
  const [isSaving, setIsSaving] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();
  if (!resource) {
    return null;
  }

  const OutOfOfficeEditButtonComponent = resource.withRelationship(fr.relationships.outOfOffice.name, (rel) => {
    return (
      <FDPrimaryActionButton
        label={rel.meta('prompt')}
        size={'sm'}
        onClick={() => {
          openModal(modalConsts.outOfOfficeModal, [{ outOfOfficeSelfLink: rel.link('related').url() }]);
        }}
      />
    );
  }, () => null);

  const PlannerTransitionButtonComponent = resource.withRelationship(fr.relationships.plannerTransition.name, (rel) => {
    return (
      <FDPrimaryActionButton
        label={rel.meta('prompt')}
        size={'sm'}
        onClick={() => openPlannerTransitionDialog(rel)}
      />
    );
  }, () => null);

  const filteredTeamCoachOptions = resource.meta().field('teamCoach')
    ?.options()
    .list()
    .filter(option => option.attr('group') === resource.attr('role'));
  const teamCoachOptions = filteredTeamCoachOptions?.map(option => ({
    prompt: option.attr('prompt'),
    id: option.attr('id'),
  }));

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      await resource.submit();
      refresh();
      handleClose();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to save agent');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FDDialog closeOnBackdropClick dialogWidth={'lg'} open onClose={handleClose}>
      <FDDialogTitle value={isAdd ? 'Add user' : 'Edit user'} />
      <FDDialogContent>
        <FDStack direction={'column'} spacing={3}>
          {!isAdd ? (
            <FDStack direction={'column'}>
              <FDBodyText value={`Date Created: ${moment(resource.attr('createdAt')).format('YYYY-MM-DD')}`} />
              <FDBodyText value={`Agent ID: ${resource.id()}`} />
              <FDBodyText value={`User ID: ${resource.attr('userId')}`} />
              {
                resource?.withRelationship(
                  fr.relationships.openReplays.name,
                  (rel) => {
                    return (
                      <a
                        target={'_blank'}
                        href={rel.link('related').url()}
                      >
                        Open Replay sessions
                      </a>
                    );
                  },
                  () => null,
                )
              }
            </FDStack>
          ) : null}
          <FDStack direction={'column'} spacing={1}>
            <FDStack direction={'column'} spacing={2}>
              <FDStack direction={'row'} spacing={1}>
                {OutOfOfficeEditButtonComponent}
                {PlannerTransitionButtonComponent}
              </FDStack>
              <FDStack direction={'row'} spacing={1}>
                <MetaControlField attr={'firstName'} />
                <MetaControlField attr={'lastName'} />
                <MetaControlField attr={'certificationTitle'} />
              </FDStack>
            </FDStack>
            <MetaControlField attr={'emailAddress'} />
            <MetaControlField
              attr={'role'}
              onChange={(value) => {
                if (value === LEAD_PLANNER && (resource.attr('certificationTitle') === '' || resource.attr('certificationTitle') === undefined || resource.attr('certificationTitle'.trim() === 'CFP'))) {
                  handleChange('certificationTitle', 'CFP®');
                }
              }}
            />
            <MetaControlField attr={'plannerTier'} />
            {teamCoachOptions?.length > 0
              ? <FDSelectInput
                label={resource.meta().field('teamCoach').attr('prompt')}
                value={resource.attr('teamCoach')}
                options={teamCoachOptions}
                onChange={value => handleChange('teamCoach', value)}
                required
              /> : null}
            <MetaControlField attr={'biography'} />
            <MetaControlField attr={'calendarURL'} />
            <MetaControlField attr={'zoomUrl'} />
            <MetaControlField attr={'chilipiperCalendarLink'} />
            <FDStack direction={'row'} spacing={1}>
              <MetaControlField attr={'isSaveAgent'} />
              <MetaControlField attr={'isTestUser'} />
              <MetaControlField attr={'isWaitListAgent'} />
            </FDStack>
          </FDStack>
        </FDStack>
      </FDDialogContent>
      <FDDialogActions>
        <FDPrimaryActionButton
          label={'Save'}
          test="save-user-button"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={isSaving}
        />
      </FDDialogActions>
    </FDDialog>
  );
};

export default AgentDialogLayout;
